import { useQuery } from "@tanstack/react-query";
import { db } from "@/lib/supabaseQuery";
import { queryKeys } from "@/lib/queryKeys";
import type { Tables } from "@/integrations/supabase/types";

export type MembershipPlan = Tables<'users_membership_plans'>;

export interface PlanGatewaySummary {
  gateway_account_id: string;
  is_enabled: boolean;
  external_price_id: string | null;
  payment_gateway_accounts: {
    provider: string;
    currency: string;
    test_mode: boolean;
    status: string;
  } | null;
}

export type MembershipPlanWithGateways = MembershipPlan & {
  users_membership_plans_gateways?: PlanGatewaySummary[];
};

export interface UseMembershipPlansOptions {
  /** When true, includes inactive/legacy plans (default: false) */
  includeInactive?: boolean;
}

export function useMembershipPlans(options: UseMembershipPlansOptions = {}) {
  const { includeInactive = false } = options;

  return useQuery({
    queryKey: [...queryKeys.settings.all, 'membership-plans', { includeInactive }],
    queryFn: async () => {
      let query = db.raw
        .from('users_membership_plans')
        // Gateway metadata is read through the public-safe view — the base
        // payment_gateway_accounts table is admin-only (no key/account leakage).
        .select('*, users_membership_plans_gateways(gateway_account_id, is_enabled, external_price_id, payment_gateway_accounts:payment_gateway_accounts_public(provider, currency, test_mode, status))')
        .order('price_monthly', { ascending: true });

      if (!includeInactive) {
        query = query.eq('active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as MembershipPlanWithGateways[];
    },
  });
}
